import { FC, useMemo } from 'react';
import { useFieldState } from 'informed';
import { parsePhoneNumber } from 'libphonenumber-js';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import {
  composeValidators,
  numberPhoneValidator,
  required,
} from 'helpers/validators';
import { Input } from './Input';
import { Select } from './Select';
import { countriesWithPhoneNumber } from 'helpers/countriesWithPhoneNumber';
import { maskPhone } from 'helpers/masks';
import { device } from 'components/styles/MediaQuery';

export const validatePhone = composeValidators(required, numberPhoneValidator);

interface InputPhoneNumberProps {
  field: string;
  initialValue?: string;
  initialCodeValue?: string;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  withValidate?: boolean;
  disabled?: boolean;
  error?: string;
}

export const InputPhoneNumber: FC<InputPhoneNumberProps> = ({
  field,
  withValidate,
  initialValue,
  initialCodeValue,
  disabled,
  ...props
}) => {
  const { t } = useTranslation();
  const fieldCodeState = useFieldState<string | undefined>(`${field}.code`);

  const currentCountry = useMemo(() => {
    if (fieldCodeState == null || fieldCodeState.value == null) return;

    return fieldCodeState.value;
  }, [fieldCodeState]);

  const countriesOptions = countriesWithPhoneNumber
    .sort((a, b) => {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    })
    .map(({ name, dialCode, iso2, flagUrl }) => ({
      name: `+${dialCode} | ${name}`,
      nameS: `+${dialCode} | ${name}`,
      nameV: `+${dialCode}`,
      value: iso2,
      icon: flagUrl,
    }));

  const {
    country: initialValueCountry,
    nationalNumber: initialValueNationalNumber,
  } = useMemo(() => {
    if (initialValue == null && initialCodeValue != null) {
      return {
        country: initialCodeValue,
        nationalNumber: undefined,
      };
    }

    if (initialValue == null)
      return {
        country: undefined,
        nationalNumber: undefined,
      };

    return parsePhoneNumber(initialValue);
  }, [initialValue, initialCodeValue]);

  return (
    <ScInputPhoneNumber>
      <Select
        initialValue={initialValueCountry}
        disabled={disabled}
        animatedPlaceholder="Code"
        field={`${field}.code`}
        options={countriesOptions}
        validate={required}
        search
        withBorderMode
        selectedOptionWithoutIcon
        minOptionHeight={48}
      />
      <Input
        {...props}
        key={currentCountry}
        initialValue={initialValueNationalNumber as string}
        disabled={disabled}
        field={`${field}.number`}
        animatedPlaceholder={t('glossary:number')}
        mask={(v) => {
          return maskPhone(v?.toString(), currentCountry);
        }}
        validate={(v) => {
          return validatePhone(v, currentCountry);
        }}
      />
    </ScInputPhoneNumber>
  );
};

const ScInputPhoneNumber = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 8px;

  @media (${device.mobileS}) {
    grid-template-columns: 120px auto;
  }
`;

export const ScInputNameSurname = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  @media (${device.mobileS}) {
    grid-template-columns: 1fr;
  }
`;

export const ScAddressInput = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  @media (${device.mobileS}) {
    grid-template-columns: 1fr;
  }
`;
